/**
 * InvoiceStatCards — premium animated KPI cards for the Invoices dashboard.
 * Computes metrics from the invoices array on each render (memoized in parent).
 * Hover lift + glow + animated counter for that "alive" feel without overdoing it.
 */
import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign, CheckCircle2, AlertTriangle, Clock, FileText,
} from 'lucide-react';

const CARDS = [
  {
    key: 'outstanding',
    label: 'Outstanding',
    icon: DollarSign,
    tint: '#0073ea',
    bg: 'linear-gradient(135deg, rgba(0,115,234,0.10) 0%, rgba(0,115,234,0.02) 100%)',
    ring: 'rgba(0,115,234,0.18)',
  },
  {
    key: 'paidThisMonth',
    label: 'Paid This Month',
    icon: CheckCircle2,
    tint: '#16a34a',
    bg: 'linear-gradient(135deg, rgba(22,163,74,0.10) 0%, rgba(22,163,74,0.02) 100%)',
    ring: 'rgba(22,163,74,0.18)',
  },
  {
    key: 'overdue',
    label: 'Overdue',
    icon: AlertTriangle,
    tint: '#dc2626',
    bg: 'linear-gradient(135deg, rgba(220,38,38,0.10) 0%, rgba(220,38,38,0.02) 100%)',
    ring: 'rgba(220,38,38,0.18)',
  },
  {
    key: 'upcoming',
    label: 'Upcoming',
    icon: Clock,
    tint: '#f59e0b',
    bg: 'linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(245,158,11,0.02) 100%)',
    ring: 'rgba(245,158,11,0.18)',
  },
  {
    key: 'drafts',
    label: 'Drafts',
    icon: FileText,
    tint: '#64748b',
    bg: 'linear-gradient(135deg, rgba(100,116,139,0.10) 0%, rgba(100,116,139,0.02) 100%)',
    ring: 'rgba(100,116,139,0.18)',
  },
];

function useAnimatedNumber(target, duration = 600) {
  const [value, setValue] = useState(target);
  useEffect(() => {
    const from = value;
    const delta = target - from;
    if (Math.abs(delta) < 0.01) return undefined;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(from + delta * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return value;
}

function fmtMoney(n) {
  if (n == null) return '$0';
  return n.toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
  });
}

function StatCard({ card, value, count, onClick, active }) {
  const Icon = card.icon;
  const animated = useAnimatedNumber(value, 700);
  return (
    <motion.button
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 350, damping: 25 }}
      onClick={onClick}
      data-testid={`invoice-stat-${card.key}`}
      style={{
        all: 'unset',
        cursor: 'pointer',
        padding: '18px 20px',
        background: card.bg,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: `1px solid ${active ? card.tint : '#e5e7eb'}`,
        borderRadius: 16,
        boxShadow: active
          ? `0 8px 24px -8px ${card.ring}, 0 0 0 3px ${card.ring}`
          : '0 1px 2px rgba(15,23,42,0.04)',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div
          style={{
            width: 32, height: 32, borderRadius: 10,
            background: `${card.tint}1f`, color: card.tint,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon size={16} />
        </div>
        {count != null && (
          <span
            style={{
              fontSize: 11, fontWeight: 600, color: card.tint,
              background: `${card.tint}14`, padding: '3px 8px', borderRadius: 999,
            }}
          >
            {count}
          </span>
        )}
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {card.label}
        </div>
        <div
          style={{
            marginTop: 4, fontSize: 22, fontWeight: 700, color: '#0f172a',
            fontVariantNumeric: 'tabular-nums', lineHeight: 1.15,
          }}
        >
          {fmtMoney(animated)}
        </div>
      </div>
    </motion.button>
  );
}

export default function InvoiceStatCards({ invoices, activeStat, onPickStat }) {
  const metrics = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    let outstanding = 0;
    let paidThisMonth = 0;
    let overdueAmt = 0; let overdueCount = 0;
    let upcomingAmt = 0; let upcomingCount = 0;
    let draftAmt = 0; let draftCount = 0;

    invoices.forEach((inv) => {
      const total = Number(inv.total || 0);
      const paid = (inv.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
      const due = inv.dueDate ? new Date(inv.dueDate) : null;
      const status = inv.status;
      const balance = Math.max(0, total - paid);

      if (status === 'draft') {
        draftAmt += total; draftCount += 1;
      } else if (status === 'paid') {
        const paidAt = inv.paidAt ? new Date(inv.paidAt) : new Date(inv.updatedAt || inv.createdAt);
        if (paidAt >= monthStart) paidThisMonth += paid || total;
      } else {
        // pending / partial / sent — unpaid balance contributes to outstanding
        outstanding += balance;
        if (due && due < now) {
          overdueAmt += balance; overdueCount += 1;
        } else if (due && (due - now) <= 7 * 24 * 60 * 60 * 1000) {
          upcomingAmt += balance; upcomingCount += 1;
        }
      }
    });

    return {
      outstanding: { value: outstanding },
      paidThisMonth: { value: paidThisMonth },
      overdue: { value: overdueAmt, count: overdueCount || null },
      upcoming: { value: upcomingAmt, count: upcomingCount || null },
      drafts: { value: draftAmt, count: draftCount || null },
    };
  }, [invoices]);

  return (
    <div
      data-testid="invoice-stat-cards"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 14, marginBottom: 22,
      }}
    >
      {CARDS.map((c) => {
        const m = metrics[c.key];
        return (
          <StatCard
            key={c.key}
            card={c}
            value={m.value}
            count={m.count}
            active={activeStat === c.key}
            onClick={() => onPickStat?.(c.key)}
          />
        );
      })}
    </div>
  );
}
